import { Handle, Position } from '@xyflow/react';

export const NODE_WIDTH = 280;
export const NODE_HEIGHT = 160;
export const SWIMLANE_HEIGHT = 200;

const OVAL_RX = (NODE_WIDTH * 0.6) / 2 - 10;
const OVAL_RY = (NODE_HEIGHT * 0.6) / 2 - 10;

const hiddenHandleStyle = {
  opacity: 0,
  width: 8,
  height: 8,
  minWidth: 8,
  minHeight: 8,
  border: 'none',
  background: 'transparent',
};

function DisplayHandles() {
  // Both source + target on every side so persisted connector handle ids
  // (left/right/top/bottom) always resolve — same coverage as the editable page.
  return (
    <>
      <Handle type="source" position={Position.Top} id="top" style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Top} id="top" style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Bottom} id="bottom" style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Left} id="left" style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Left} id="left" style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Right} id="right" style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Right} id="right" style={hiddenHandleStyle} />
    </>
  );
}

function MappedControlBadges({ mappedControls, offsetForHexagons = false }) {
  if (!Array.isArray(mappedControls) || mappedControls.length === 0) return null;
  return (
    <div
      className="absolute z-40 flex flex-col gap-1"
      style={{
        bottom: '0',
        left: offsetForHexagons ? '35px' : '0',
        transform: 'translate(-20%, 50%)',
        pointerEvents: 'none',
      }}
    >
      {mappedControls.map((control, index) => {
        const label = String(
          control?.controlDisplayId ?? control?.control_display_id ?? control?.controlId ?? control?.control_id ?? ''
        ).trim();
        if (!label) return null;
        const key = String(control?.controlId ?? control?.control_id ?? `${label}-${index}`);
        return (
          <div key={key} title={`Control: ${label}`}>
            <svg width="90" height="30" viewBox="0 0 90 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="90" height="30" rx="6" ry="6" fill="#0d9488" stroke="#0f766e" strokeWidth="1.5" />
              <path
                d="M6 11 L13 7 L20 11 L20 16 C20 19 13 22 13 22 C13 22 6 19 6 16 Z"
                fill="none"
                stroke="#ffffff"
                strokeWidth="1.5"
              />
              <text
                x="28"
                y="18"
                fill="#ffffff"
                fontSize="10"
                fontWeight="600"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {label.length > 10 ? label.substring(0, 10) : label}
              </text>
            </svg>
          </div>
        );
      })}
    </div>
  );
}

function normalizeRiskLevelValue(risk) {
  const rawLevel = String(risk?.riskLevel ?? risk?.risk_level ?? '').trim().toLowerCase();
  if (rawLevel === 'high') return 'High';
  if (rawLevel === 'medium') return 'Medium';
  if (rawLevel === 'low') return 'Low';
  return '';
}

function getControlDisplayLabel(control) {
  if (!control || typeof control !== 'object') return '';
  const clientId = String(control.client_control_id ?? control.clientControlId ?? '').trim();
  if (clientId) return clientId;
  return String(control.control_id ?? control.controlId ?? '').trim();
}

function isKeyControlSignificance(control) {
  if (!control || typeof control !== 'object' || control.__legacyLabel) return false;
  return String(control.significance ?? '').trim().toLowerCase() === 'key';
}

function collectControlsFromNodeRisks(risks) {
  if (!Array.isArray(risks) || risks.length === 0) return [];
  const seen = new Set();
  const controls = [];
  risks.forEach((risk) => {
    const riskControls = risk?.controls;
    if (!Array.isArray(riskControls)) return;
    riskControls.forEach((control) => {
      const dedupeKey = String(
        control?.id ?? control?.control_id ?? control?.controlId ?? getControlDisplayLabel(control) ?? ''
      ).trim();
      if (!dedupeKey || seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      controls.push(control);
    });
  });
  return controls;
}

function normalizeNodeDocuments(documentsFromApi) {
  if (!Array.isArray(documentsFromApi) || documentsFromApi.length === 0) return [];
  const out = [];
  for (const d of documentsFromApi) {
    if (!d || typeof d !== 'object') continue;
    const id = typeof d.id === 'string' ? d.id.trim() : '';
    const name = typeof d.name === 'string' ? d.name.trim() : '';
    if (!id && !name) continue;
    const rawType = d.docType ?? d.doc_type ?? null;
    let docType = null;
    if (typeof rawType === 'string') {
      const trimmed = rawType.trim();
      docType = trimmed === '' ? null : trimmed;
    }
    out.push({ id, name, docType });
  }
  return out;
}

function resolveSystemDisplayEntries(systemsFromApi) {
  if (!Array.isArray(systemsFromApi) || systemsFromApi.length === 0) return [];
  return systemsFromApi
    .map((s) => {
      const name = typeof s?.name === 'string' ? s.name.trim() : '';
      if (!name) return null;
      return { name, isKeySystem: s.keySystem === true };
    })
    .filter(Boolean);
}

function SystemCylinderIcon({ name, isKeySystem, palette = 'rectangle' }) {
  let fillTop;
  let fillBody;
  let stroke;
  let textFill;
  if (palette === 'decision') {
    fillTop = '#a78bfa';
    fillBody = '#8b5cf6';
    stroke = '#6d28d9';
    textFill = '#ffffff';
  } else if (isKeySystem) {
    fillTop = '#8b5cf6';
    fillBody = '#7c3aed';
    stroke = '#5b21b6';
    textFill = '#ffffff';
  } else {
    fillTop = '#bfdbfe';
    fillBody = '#dbeafe';
    stroke = '#93c5fd';
    textFill = '#1e3a8a';
  }
  const strokeWidth = palette === 'decision' || isKeySystem ? 2 : 1.5;
  const displayName = name.length > 10 ? `${name.slice(0, 9)}…` : name;
  return (
    <svg width="80" height="48" viewBox="0 0 80 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="40" cy="7" rx="35" ry="7" fill={fillBody} stroke={stroke} strokeWidth={strokeWidth} />
      <rect x="5" y="7" width="70" height="33" fill={fillBody} />
      <ellipse cx="40" cy="40" rx="35" ry="7" fill={fillTop} stroke={stroke} strokeWidth={strokeWidth} />
      <ellipse cx="40" cy="7" rx="35" ry="7" fill={fillTop} stroke={stroke} strokeWidth={strokeWidth} />
      <text
        x="40"
        y={isKeySystem && palette !== 'decision' ? 21 : 24}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textFill}
        fontSize="9"
        fontWeight={palette === 'decision' ? 700 : 600}
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        <title>{name}</title>
        {displayName}
      </text>
      {isKeySystem && palette !== 'decision' ? (
        <text
          x="40"
          y="35"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fde047"
          fontSize="6"
          fontWeight="700"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          KEY
        </text>
      ) : null}
    </svg>
  );
}

function SystemCylinders({ systemEntries, palette = 'rectangle' }) {
  if (!Array.isArray(systemEntries) || systemEntries.length === 0) return null;
  const MAX_VISIBLE = 4;
  const visible = systemEntries.slice(0, MAX_VISIBLE);
  const overflow = systemEntries.length - visible.length;
  return (
    <div
      className="absolute"
      style={{
        bottom: '0',
        right: '0',
        transform: 'translate(20%, 50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        pointerEvents: 'none',
      }}
    >
      {visible.map((entry, idx) => (
        <SystemCylinderIcon
          key={`${entry.name}-${idx}`}
          name={entry.name}
          isKeySystem={entry.isKeySystem}
          palette={palette}
        />
      ))}
      {overflow > 0 ? (
        <div
          title={systemEntries.slice(MAX_VISIBLE).map((e) => e.name).join(', ')}
          className="rounded-full border border-blue-300 bg-white px-1.5 py-0.5 text-[10px] font-bold text-blue-900 shadow"
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}

function DocumentFolderIcon({ name, isReport = false }) {
  const displayName = name.length > 10 ? `${name.slice(0, 10)}…` : name;
  const fill = isReport ? '#fef3c7' : '#dcfce7';
  const stroke = isReport ? '#f59e0b' : '#86efac';
  const tabFill = isReport ? '#fde047' : '#bbf7d0';
  const textFill = isReport ? '#b45309' : '#15803d';
  return (
    <svg width="70" height="52" viewBox="0 0 70 55" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M 10 20 L 30 20 L 35 15 L 60 15 L 60 50 L 10 50 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
      <path d="M 10 20 L 60 20 L 60 25 L 10 25 Z" fill={tabFill} />
      <text
        x="35"
        y="38"
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textFill}
        fontSize="9"
        fontWeight="600"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        <title>{name}</title>
        {displayName}
      </text>
    </svg>
  );
}

function DocumentFolders({ documents }) {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  const MAX_VISIBLE = 4;
  const FOLDER_STEP = 58;
  const visible = documents.slice(0, MAX_VISIBLE);
  const overflow = documents.length - visible.length;
  return (
    <div className="absolute z-40" style={{ top: '0', right: '0', width: 0, height: 0 }}>
      {visible.map((doc, index) => {
        const name = doc?.name || '';
        const isReport = typeof doc === 'object' && doc?.docType === 'report';
        return (
          <div
            key={`${name}-${index}`}
            title={name}
            style={{
              position: 'absolute',
              top: 0,
              right: index * FOLDER_STEP,
              transform: 'translate(50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <DocumentFolderIcon name={name} isReport={isReport} />
          </div>
        );
      })}
      {overflow > 0 ? (
        <div
          title={documents.slice(MAX_VISIBLE).map((d) => d?.name).filter(Boolean).join(', ')}
          className="rounded-full border border-green-300 bg-white px-1.5 py-0.5 text-[10px] font-bold text-green-900 shadow"
          style={{
            position: 'absolute',
            top: 0,
            right: visible.length * FOLDER_STEP,
            transform: 'translate(50%, -50%)',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}

function RiskBadge({ risks }) {
  if (!Array.isArray(risks) || risks.length === 0) return null;
  let highest = 'Low';
  if (risks.some((r) => normalizeRiskLevelValue(r) === 'High')) highest = 'High';
  else if (risks.some((r) => normalizeRiskLevelValue(r) === 'Medium')) highest = 'Medium';
  return (
    <div
      className="absolute -left-3 -top-3 z-50"
      style={{ pointerEvents: 'none' }}
      title={`${risks.length} risk${risks.length > 1 ? 's' : ''} identified`}
    >
      <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 4 L26 24 L2 24 Z"
          fill={highest === 'High' ? '#dc2626' : highest === 'Medium' ? '#f59e0b' : '#eab308'}
          stroke={highest === 'High' ? '#991b1b' : highest === 'Medium' ? '#d97706' : '#ca8a04'}
          strokeWidth="1.5"
        />
        <text x="14" y="19" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
          !
        </text>
      </svg>
      {risks.length > 1 ? (
        <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-700 text-xs font-bold text-white">
          {risks.length}
        </span>
      ) : null}
    </div>
  );
}

function ControlHexagons({ controls }) {
  if (!Array.isArray(controls) || controls.length === 0) return null;
  const MAX_VISIBLE = 4;
  const visible = controls.slice(0, MAX_VISIBLE);
  const overflow = controls.length - visible.length;
  return (
    <div className="absolute z-40" style={{ bottom: '0', left: '0', width: 0, height: 0 }}>
      {visible.map((control, index) => {
        const controlLabel = control.__legacyLabel || getControlDisplayLabel(control);
        if (!controlLabel) return null;
        const controlKey = String(
          control.id ?? control.control_id ?? control.controlId ?? `${controlLabel}-${index}`
        );
        const isKeyControl = isKeyControlSignificance(control);
        return (
          <div
            key={controlKey}
            title={`Control: ${controlLabel}`}
            style={{
              position: 'absolute',
              bottom: index * 34,
              left: 0,
              transform: 'translate(-50%, 50%)',
              pointerEvents: 'none',
            }}
          >
            <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon
                points="20,30 35,15 50,30 50,40 35,55 20,40"
                fill={isKeyControl ? '#ea580c' : '#f3e8ff'}
                stroke={isKeyControl ? '#c2410c' : '#c084fc'}
                strokeWidth={isKeyControl ? '2' : '1.5'}
              />
              <text
                x="35"
                y={isKeyControl ? '30' : '35'}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isKeyControl ? '#ffffff' : '#6b21a8'}
                fontSize="9"
                fontWeight="600"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {controlLabel.length > 8 ? controlLabel.substring(0, 8) : controlLabel}
              </text>
              {isKeyControl ? (
                <text
                  x="35"
                  y="44"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fde047"
                  fontSize="7"
                  fontWeight="700"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  KEY
                </text>
              ) : null}
            </svg>
          </div>
        );
      })}
      {overflow > 0 ? (
        <div
          title={`${overflow} more control${overflow > 1 ? 's' : ''}`}
          className="rounded-full border border-purple-300 bg-white px-1.5 py-0.5 text-[10px] font-bold text-purple-900 shadow"
          style={{
            position: 'absolute',
            bottom: visible.length * 34 + 8,
            left: 0,
            transform: 'translate(-50%, 50%)',
            pointerEvents: 'none',
          }}
        >
          +{overflow}
        </div>
      ) : null}
    </div>
  );
}

function DisplaySwimlaneNode({ data }) {
  const laneHeight = Number.isFinite(data.height) && data.height > 0 ? data.height : SWIMLANE_HEIGHT;
  return (
    <div
      style={{
        width: data.width,
        height: laneHeight,
        backgroundColor: data.index % 2 === 0 ? 'rgba(249, 250, 251, 0.3)' : 'rgba(255, 255, 255, 0.3)',
        borderBottom: '2px solid #d1d5db',
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 16,
          top: 16,
          fontWeight: 600,
          color: '#1f2937',
          fontSize: 14,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '4px 8px',
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {data.label}
      </div>
    </div>
  );
}

function DisplayRectangleNode({ data }) {
  const riskControls = collectControlsFromNodeRisks(data.risks);
  const legacySoxControl = String(data.soxControl || '').trim();
  const controlsToRender =
    riskControls.length > 0 ? riskControls : legacySoxControl ? [{ __legacyLabel: legacySoxControl }] : [];
  const documentList = normalizeNodeDocuments(data.documents).filter((d) => d.name);
  const systemEntries = resolveSystemDisplayEntries(data.systems);
  const isMapped = data.isMapped !== false;
  const hasSoxControl = controlsToRender.length > 0;

  return (
    <div className="relative" style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}>
      <DisplayHandles />
      <RiskBadge risks={data.risks} />
      <div
        className="relative flex h-full w-full flex-col justify-center overflow-hidden p-3"
        style={{
          backgroundColor: isMapped ? '#ffffff' : '#fef3c7',
          border: isMapped ? '2px solid #3b82f6' : '2px solid #fbbf24',
          borderRadius: 8,
        }}
      >
        <div className="mb-2 line-clamp-2 text-sm font-semibold text-gray-900">
          {data.nodeDescription || 'Process step'}
        </div>
        {data.actor ? (
          <div className="text-xs text-blue-600">
            <strong>Actor:</strong> {data.actor}
          </div>
        ) : null}
      </div>
      {systemEntries.length > 0 ? <SystemCylinders systemEntries={systemEntries} palette="rectangle" /> : null}
      <ControlHexagons controls={controlsToRender} />
      {documentList.length > 0 ? <DocumentFolders documents={documentList} /> : null}
      <MappedControlBadges mappedControls={data.mappedControls} offsetForHexagons={hasSoxControl} />
    </div>
  );
}

function DisplayDecisionNode({ data }) {
  // Match FlowchartViewer DecisionNode: SOX text inside diamond (not hexagons),
  // systems use rectangle palette, mappedControls as teal badges.
  const riskControls = collectControlsFromNodeRisks(data.risks);
  const legacySoxControl = String(data.soxControl || '').trim();
  const primaryControlLabel =
    riskControls.length > 0 ? getControlDisplayLabel(riskControls[0]) : legacySoxControl;
  const documentList = normalizeNodeDocuments(data.documents).filter((d) => d.name);
  const systemEntries = resolveSystemDisplayEntries(data.systems);
  const isMapped = data.isMapped !== false;

  return (
    <div className="relative" style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}>
      <DisplayHandles />
      <RiskBadge risks={data.risks} />
      <svg width={NODE_WIDTH} height={NODE_HEIGHT} className="absolute inset-0">
        <polygon
          points={`${NODE_WIDTH / 2},10 ${NODE_WIDTH - 10},${NODE_HEIGHT / 2} ${NODE_WIDTH / 2},${NODE_HEIGHT - 10} 10,${NODE_HEIGHT / 2}`}
          fill={isMapped ? '#ffffff' : '#fef3c7'}
          stroke={isMapped ? '#3b82f6' : '#fbbf24'}
          strokeWidth="2.5"
        />
      </svg>
      <div
        className="absolute text-center"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          padding: 8,
        }}
      >
        <div className="mb-1 text-xs font-semibold leading-snug text-gray-900">
          {data.nodeDescription || 'Decision'}
        </div>
        {primaryControlLabel ? (
          <div style={{ fontSize: 9, color: '#9333ea', marginTop: 2 }}>SOX: {primaryControlLabel}</div>
        ) : null}
      </div>
      {documentList.length > 0 ? <DocumentFolders documents={documentList} /> : null}
      {systemEntries.length > 0 ? <SystemCylinders systemEntries={systemEntries} palette="rectangle" /> : null}
      <MappedControlBadges mappedControls={data.mappedControls} />
    </div>
  );
}

function DisplayOvalNode({ data }) {
  const isStart = String(data.nodeType || '').toLowerCase() === 'start';
  return (
    <div className="relative" style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}>
      <DisplayHandles />
      <svg width={NODE_WIDTH} height={NODE_HEIGHT} style={{ overflow: 'visible' }}>
        <ellipse
          cx={NODE_WIDTH / 2}
          cy={NODE_HEIGHT / 2}
          rx={OVAL_RX}
          ry={OVAL_RY}
          fill={isStart ? '#d1fae5' : '#fee2e2'}
          stroke={isStart ? '#10b981' : '#ef4444'}
          strokeWidth={2}
        />
        <text
          x={NODE_WIDTH / 2}
          y={NODE_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={isStart ? '#047857' : '#dc2626'}
          fontSize="14"
          fontWeight="600"
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
        >
          {isStart ? 'Start' : 'End'}
        </text>
      </svg>
    </div>
  );
}

export const combinedProcessDisplayNodeTypes = {
  swimlane: DisplaySwimlaneNode,
  rectangle: DisplayRectangleNode,
  decision: DisplayDecisionNode,
  oval: DisplayOvalNode,
};
